import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, CheckCheck, Clock, CheckCircle2, AlertCircle, Laptop } from "lucide-react";
import api from "../../api/axios";
import {
  isPushSupported,
  getNotificationPermission,
  getSubscriptionStatus,
  subscribeUserToPush,
  unsubscribeUserFromPush,
} from "../../services/pushNotificationService";
import "./NotificationBell.css";

const NotificationBell = () => {
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  // Web Push Subscription state
  const [pushStatus, setPushStatus] = useState({
    supported: isPushSupported(),
    permission: getNotificationPermission(),
    isSubscribed: false,
    loading: false,
    error: null,
  });

  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  // Fetch unread count
  const fetchUnreadCount = async () => {
    try {
      const res = await api.get("/notifications/unread-count");
      if (res.data && typeof res.data.unread_count === "number") {
        setUnreadCount(res.data.unread_count);
      }
    } catch (err) {
      // Silently handle if unauthenticated or network failure
    }
  };

  // Fetch recent notifications
  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const res = await api.get("/notifications?limit=10");
      if (res.data && Array.isArray(res.data.notifications)) {
        setNotifications(res.data.notifications);
        setUnreadCount(res.data.unread_count ?? 0);
      }
    } catch (err) {
      // Handled globally
    } finally {
      setLoading(false);
    }
  };

  // Check Web Push subscription status
  const updatePushStatus = async () => {
    try {
      const status = await getSubscriptionStatus();
      setPushStatus((prev) => ({
        ...prev,
        supported: status.supported,
        permission: status.permission,
        isSubscribed: status.isSubscribed,
      }));
    } catch (err) {
      console.warn("Failed to update push status:", err);
    }
  };

  // Polling unread count every 30 seconds
  useEffect(() => {
    fetchUnreadCount();
    updatePushStatus();
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  // Handle service worker postMessage push navigation events
  useEffect(() => {
    const handleServiceWorkerMessage = (event) => {
      if (event.data && event.data.type === "CRM_PUSH_NAVIGATE" && event.data.url) {
        navigate(event.data.url);
      }
    };

    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.addEventListener("message", handleServiceWorkerMessage);
    }
    return () => {
      if ("serviceWorker" in navigator) {
        navigator.serviceWorker.removeEventListener("message", handleServiceWorkerMessage);
      }
    };
  }, [navigate]);

  const handleToggle = () => {
    if (!isOpen) {
      fetchNotifications();
      updatePushStatus();
    }
    setIsOpen((prev) => !prev);
  };

  const handleTogglePush = async () => {
    setPushStatus((prev) => ({ ...prev, loading: true, error: null }));
    try {
      if (pushStatus.isSubscribed) {
        await unsubscribeUserFromPush();
      } else {
        await subscribeUserToPush();
      }
      await updatePushStatus();
    } catch (err) {
      console.error("Push toggle error:", err);
      setPushStatus((prev) => ({
        ...prev,
        error: err.message || "Failed to update notification settings",
      }));
    } finally {
      setPushStatus((prev) => ({ ...prev, loading: false }));
    }
  };

  const handleMarkAsRead = async (notification, e) => {
    if (e) e.stopPropagation();
    try {
      await api.patch(`/notifications/${notification.id}/read`);
      setNotifications((prev) =>
        prev.map((n) => (n.id === notification.id ? { ...n, is_read: true } : n))
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (err) {
      // Handled globally
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await api.patch("/notifications/read-all");
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (err) {
      // Handled globally
    }
  };

  const handleNotificationClick = async (notification) => {
    if (!notification.is_read) {
      try {
        await api.patch(`/notifications/${notification.id}/read`);
        setUnreadCount((prev) => Math.max(0, prev - 1));
      } catch (err) {
        // Continue navigation regardless
      }
    }

    setIsOpen(false);

    // Route to follow-up or appropriate module based on entity type
    const isDealFollowup =
      notification.entity_type === "deal_followup" ||
      Boolean(notification.deal_id);

    const isLeadFollowup =
      notification.entity_type === "lead_followup" ||
      Boolean(notification.lead_id) ||
      Boolean(notification.followup_id) ||
      (notification.type && notification.type.includes("followup")) ||
      notification.title?.toLowerCase().includes("follow-up") ||
      notification.title?.toLowerCase().includes("followup") ||
      notification.message?.toLowerCase().includes("follow-up");

    if (isDealFollowup) {
      navigate("/deals", {
        state: {
          openDealId: notification.deal_id || notification.entity_id,
          openFollowUp: true,
          followupId: notification.followup_id || notification.entity_id,
        },
      });
      return;
    }

    if (isLeadFollowup) {
      const leadId =
        notification.lead_id ||
        (notification.entity_type === "lead" ? notification.entity_id : null);

      if (leadId) {
        navigate("/leads", {
          state: {
            openLeadId: leadId,
            openFollowUp: true,
            followupId: notification.followup_id || notification.entity_id,
          },
        });
        return;
      }

      if (notification.task_id) {
        navigate("/tasks", {
          state: {
            openTaskId: notification.task_id,
            openFollowUp: true,
            followupId: notification.followup_id,
          },
        });
        return;
      }

      navigate("/leads", {
        state: {
          openFollowUp: true,
          followupId: notification.followup_id || notification.entity_id,
        },
      });
      return;
    }

    // Default routing
    if (notification.task_id || notification.entity_type === "task") {
      navigate("/tasks", {
        state: { openTaskId: notification.task_id },
      });
    } else if (notification.entity_type === "lead") {
      navigate("/leads", {
        state: { openLeadId: notification.entity_id },
      });
    } else if (notification.entity_type === "deal") {
      navigate("/deals", {
        state: { openDealId: notification.entity_id },
      });
    } else {
      navigate("/tasks");
    }
  };

  const formatTimeAgo = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  return (
    <div className="notification-bell-container" ref={dropdownRef}>
      <button
        className="notification-bell-btn"
        onClick={handleToggle}
        aria-label="Portal Notifications"
        title="Notifications"
      >
        <Bell size={20} className="bell-icon" />
        {unreadCount > 0 && (
          <span className="notification-badge">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="notification-dropdown">
          <div className="notification-dropdown-header">
            <div className="header-title-box">
              <span className="header-title">Notifications</span>
              {unreadCount > 0 && (
                <span className="unread-counter-tag">{unreadCount} new</span>
              )}
            </div>
            {unreadCount > 0 && (
              <button
                className="mark-all-read-btn"
                onClick={handleMarkAllRead}
                title="Mark all as read"
              >
                <CheckCheck size={14} />
                <span>Mark all read</span>
              </button>
            )}
          </div>

          {/* WEB PUSH DESKTOP ALERT CONTROL BANNER */}
          {pushStatus.supported && (
            <div className="push-alert-banner">
              <div className="push-alert-info">
                <Laptop size={15} className="push-icon" />
                <span>Desktop Alerts</span>
              </div>
              {pushStatus.permission === "denied" ? (
                <span className="push-status-denied" title="Blocked in browser settings">
                  Blocked
                </span>
              ) : (
                <button
                  type="button"
                  className={`push-toggle-btn ${pushStatus.isSubscribed ? "subscribed" : ""}`}
                  onClick={handleTogglePush}
                  disabled={pushStatus.loading}
                >
                  {pushStatus.loading
                    ? "Updating..."
                    : pushStatus.isSubscribed
                    ? "Enabled ✓"
                    : "Enable"}
                </button>
              )}
            </div>
          )}
          {pushStatus.error && (
            <div className="push-alert-error">
              <AlertCircle size={13} />
              <span>{pushStatus.error}</span>
            </div>
          )}

          <div className="notification-dropdown-body">
            {loading ? (
              <div className="notification-loading-state">
                <div className="notification-spinner"></div>
                <span>Loading notifications...</span>
              </div>
            ) : notifications.length === 0 ? (
              <div className="notification-empty-state">
                <Bell size={32} className="empty-bell-icon" />
                <p className="empty-title">All caught up!</p>
                <p className="empty-subtitle">No notifications to show right now</p>
              </div>
            ) : (
              <div className="notification-items-list">
                {notifications.map((n) => (
                  <div
                    key={n.id}
                    className={`notification-item ${!n.is_read ? "unread" : ""}`}
                    onClick={() => handleNotificationClick(n)}
                  >
                    <div className="notification-item-indicator">
                      {!n.is_read ? (
                        <span className="unread-dot" title="Unread"></span>
                      ) : (
                        <CheckCircle2 size={14} className="read-check-icon" />
                      )}
                    </div>

                    <div className="notification-item-content">
                      <div className="notification-item-top">
                        <span className="notification-item-title">{n.title}</span>
                        <span className="notification-item-time">
                          <Clock size={11} />
                          {formatTimeAgo(n.created_at)}
                        </span>
                      </div>
                      <p className="notification-item-msg">{n.message}</p>
                    </div>

                    {!n.is_read && (
                      <button
                        className="quick-mark-read-btn"
                        onClick={(e) => handleMarkAsRead(n, e)}
                        title="Mark as read"
                      >
                        <CheckCheck size={14} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="notification-dropdown-footer">
            <button
              className="view-all-tasks-link"
              onClick={() => {
                setIsOpen(false);
                navigate("/tasks");
              }}
            >
              Go to My Tasks →
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
